import { memo, useEffect, useState } from "react";
import { X, CheckCircle, AlertCircle, Info } from "lucide-react";
import type { QueryStatus } from "../../types";

interface ToastProps {
  message: string | null;
  type?: "info" | "success" | "error";
  autoHide?: boolean;
  duration?: number;
  onClose?: () => void;
}

export const Toast = memo(({ 
  message, 
  type = "info", 
  autoHide = true, 
  duration = 5000,
  onClose 
}: ToastProps) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (message) {
      setVisible(true);
      
      if (autoHide && type !== "error") {
        const timer = setTimeout(() => {
          setVisible(false);
          onClose?.();
        }, duration);

        return () => clearTimeout(timer);
      }
    } else {
      setVisible(false);
    }
  }, [message, type, autoHide, duration, onClose]);

  if (!message || !visible) return null;

  const handleClose = () => {
    setVisible(false);
    onClose?.();
  };

  const typeConfig = {
    info: {
      bg: "bg-accent",
      border: "border-primary/30",
      text: "text-primary-dark",
      icon: <Info size={20} className="text-primary" />,
    },
    success: {
      bg: "bg-accent",
      border: "border-primary/30",
      text: "text-accent-foreground",
      icon: <CheckCircle size={20} className="text-accent-foreground" />,
    },
    error: {
      bg: "bg-red-50",
      border: "border-red-200",
      text: "text-red-800",
      icon: <AlertCircle size={20} className="text-red-600" />,
    },
  };

  const config = typeConfig[type];

  return (
    <div
      className="fixed right-4 z-[99999] animate-slideIn max-w-md"
      style={{
        // Показываем уведомление ниже фиксированного Header,
        // чтобы оно не перекрывалось и не перекрывало заголовки панелей
        top: "calc(var(--header-height, 4.5rem) + 1rem)",
      }}
    >
      <div
        className={`${config.bg} ${config.border} ${config.text} border rounded-lg shadow-md p-4 flex items-start gap-3`}
        role="alert"
      >
        <div className="flex-shrink-0 mt-0.5">{config.icon}</div>
        <div className="flex-1 min-w-0">
          <div className={`font-medium text-sm mb-1 ${type === "error" ? "text-red-900" : ""}`}>
            {type === "error" ? "Ошибка при выполнении запроса" : type === "success" ? "Успешно" : "Уведомление"}
          </div>
          <div className={`text-sm leading-relaxed whitespace-pre-wrap break-words ${config.text}`}>
            {message}
          </div>
        </div>
        <button
          onClick={handleClose}
          className="flex-shrink-0 p-1 rounded hover:bg-black/5 transition-colors"
          aria-label="Закрыть"
        >
          <X size={16} className={config.text} />
        </button>
      </div>
    </div>
  );
});
